<script>
  import Markdown from '../components/Markdown.svelte';
  import { onMount, tick } from 'svelte';
  import { apiFetch } from '../lib/auth.js';

  const PAGE_SIZE = 20;

  let allArticles = [];
  let page        = 0;
  let listLoad    = false;
  let listLoaded  = false;
  let listError   = '';

  let expandedId   = null;
  let expandedEl   = null;
  let selected     = null;
  let articleLoad  = false;
  let articleError = '';

  onMount(() => loadNews());

  async function loadNews() {
    if (listLoad || listLoaded) return;
    listLoad = true; listError = '';
    try {
      const data = await apiFetch('/news');
      allArticles = Array.isArray(data) ? data : [];
    } catch (e) {
      listError = e.message || 'Failed to load news';
    } finally {
      listLoad   = false;
      listLoaded = true;
    }
  }

  $: total      = allArticles.length;
  $: totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  $: articles   = allArticles.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  function goPage(delta) {
    page = Math.max(0, Math.min(totalPages - 1, page + delta));
    expandedId = null; selected = null;
  }

  async function selectArticle(id) {
    if (expandedId === id) { expandedId = null; selected = null; return; }

    expandedId   = id;
    selected     = null;
    articleLoad  = true;
    articleError = '';

    await tick();
    if (expandedEl) expandedEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

    try {
      selected = await apiFetch(`/news/${id}`);
    } catch (e) {
      articleError = e.message || 'Failed to load article';
    } finally {
      articleLoad = false;
    }
  }

  function formatDate(str) {
    if (!str) return '—';
    return new Date(str).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }
</script>

<div class="news-page">

  {#if listLoad}
    <div class="state-msg"><span class="spinner"></span> Loading...</div>
  {:else if listError}
    <div class="state-error">{listError}</div>
  {:else if allArticles.length === 0}
    <div class="state-msg empty">No articles published yet.</div>
  {:else}

    <!-- ── Table ── -->
    <div class="table-wrap">
      <table>
        <thead>
          <tr>
            <th class="col-chevron"></th>
            <th>Headline</th>
            <th class="col-author col-hide-sm">Author</th>
            <th class="col-date">Date</th>
          </tr>
        </thead>
        <tbody>
          {#each articles as article (article.id)}

            <!-- ── Summary row ── -->
            <tr
              class="summary-row"
              class:expanded={expandedId === article.id}
              on:click={() => selectArticle(article.id)}
              role="button"
              tabindex="0"
              on:keydown={e => e.key === 'Enter' && selectArticle(article.id)}
            >
              <td class="cell-chevron">
                <svg
                  class="chevron"
                  class:open={expandedId === article.id}
                  viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
                >
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </td>
              <td class="cell-headline">
                {article.headline}
                {#if article.username}
                  <span class="cell-author-inline col-show-sm">· {article.username}</span>
                {/if}
              </td>
              <td class="cell-author col-hide-sm">{article.username || '—'}</td>
              <td class="cell-date">{formatDate(article.created_at)}</td>
            </tr>

            <!-- ── Inline expanded row ── -->
            {#if expandedId === article.id}
              <tr class="expand-row" bind:this={expandedEl}>
                <td colspan="4" class="expand-cell">
                  <div class="expand-inner">

                    {#if articleLoad}
                      <div class="expand-state"><span class="spinner"></span> Loading...</div>
                    {:else if articleError}
                      <div class="expand-state error">{articleError}</div>
                    {:else if selected}

                      <div class="article-byline">
                        <span class="byline-author">{selected.username}</span>
                        <span class="byline-sep">·</span>
                        <span class="byline-date">{formatDate(selected.created_at)}</span>
                      </div>

                      {#if selected.article_image_url}
                        <img class="article-img" src={selected.article_image_url} alt={selected.headline} />
                      {/if}

                      <div class="article-body">
                        <Markdown content={selected.body} prose />
                      </div>

                    {/if}
                  </div>
                </td>
              </tr>
            {/if}

          {/each}
        </tbody>
      </table>
    </div>

    <!-- ── Pagination ── -->
    {#if totalPages > 1}
      <div class="pagination">
        <button class="page-btn" on:click={() => goPage(-1)} disabled={page === 0}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="15 18 9 12 15 6"/></svg>
          Prev
        </button>
        <div class="page-info">
          <span class="page-current">{page + 1}</span>
          <span class="page-sep">/</span>
          <span class="page-total">{totalPages}</span>
        </div>
        <button class="page-btn" on:click={() => goPage(1)} disabled={page >= totalPages - 1}>
          Next
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="9 18 15 12 9 6"/></svg>
        </button>
      </div>
    {/if}

  {/if}
</div>

<style>
  @import url('https://fonts.googleapis.com/css2?family=Rajdhani:wght@400;600;700&display=swap');

  /* ── States ── */
  .state-msg {
    display: flex; align-items: center; gap: 0.6rem; color: #2a5200;
    font-family: 'Rajdhani', sans-serif; font-size: 0.7rem;
    letter-spacing: 0.15em; text-transform: uppercase; padding: 2rem 0;
  }
  .state-msg.empty { color: #1e3d00; }
  .state-error { color: #cc6633; font-family: 'Rajdhani', sans-serif; font-size: 0.65rem; padding: 1rem 0; }
  .spinner {
    width: 14px; height: 14px; flex-shrink: 0;
    border: 2px solid #1e3d00; border-top-color: #6db800;
    border-radius: 50%; animation: spin 0.7s linear infinite;
  }
  @keyframes spin { to { transform: rotate(360deg); } }

  /* ── Table ── */
  .table-wrap { overflow: hidden; border: 1px solid #1e3d00; border-radius: 2px; margin-bottom: 0.5rem; }
  table { width: 100%; table-layout: fixed; border-collapse: collapse; font-family: 'Rajdhani', sans-serif; }
  thead tr { background: #0d1a00; border-bottom: 1px solid #1e3d00; }
  th {
    padding: 0.55rem 0.75rem; font-size: 0.5rem; font-weight: 700;
    letter-spacing: 0.25em; text-transform: uppercase; color: #3a6b00;
    text-align: left; white-space: nowrap;
  }
  .col-chevron { width: 28px; padding: 0; }
  .col-author  { width: 120px; }
  .col-date    { width: 100px; }

  .summary-row { border-bottom: 1px solid #0f1a00; cursor: pointer; transition: background 0.1s; }
  .summary-row:hover { background: #0d1a00; }
  .summary-row.expanded { background: #0d1a00; border-bottom: none; }
  .summary-row:focus-visible { outline: 1px solid #3a6b00; outline-offset: -1px; }

  td { padding: 0.6rem 0.75rem; font-size: 0.85rem; font-weight: 600; color: #c8c8c8; vertical-align: middle; }
  .cell-chevron { padding: 0 0 0 0.6rem; width: 28px; }
  .chevron { width: 16px; height: 16px; stroke: #2a5200; display: block; transition: transform 0.2s ease; }
  .chevron.open { transform: rotate(180deg); }

  .cell-headline { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 0; }
  .cell-author-inline { font-size: 0.68rem; color: #3a6b00; font-weight: 600; }
  .cell-author { color: #3a6b00; font-size: 0.78rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .cell-date   { color: #2a5200; font-size: 0.72rem; white-space: nowrap; }

  @media (max-width: 520px) {
    .col-hide-sm  { display: none; }
    .col-show-sm  { display: inline; }
  }
  @media (min-width: 521px) {
    .col-show-sm  { display: none; }
  }

  /* ── Expanded row ── */
  .expand-row { border-bottom: 1px solid #1e3d00; }
  .expand-cell { padding: 0 !important; background: #080f00; }
  .expand-inner {
    padding: 1rem 1rem 1.25rem 2.5rem;
    animation: slideDown 0.15s ease;
    display: flex; flex-direction: column; gap: 0.75rem;
  }
  @keyframes slideDown {
    from { opacity: 0; transform: translateY(-4px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  .expand-state {
    display: flex; align-items: center; gap: 0.5rem; padding: 0.5rem 0;
    font-family: 'Rajdhani', sans-serif; font-size: 0.65rem;
    letter-spacing: 0.15em; text-transform: uppercase; color: #2a5200;
  }
  .expand-state.error { color: #cc6633; }

  .article-byline {
    display: flex; align-items: center; gap: 0.35rem;
  }
  .byline-author { font-family: 'Rajdhani', sans-serif; font-weight: 700; font-size: 0.6rem; color: #3a6b00; letter-spacing: 0.15em; text-transform: uppercase; }
  .byline-sep    { color: #1e3d00; font-size: 0.55rem; }
  .byline-date   { font-family: 'Rajdhani', sans-serif; font-size: 0.6rem; color: #2a5200; letter-spacing: 0.08em; }

  .article-img {
    width: 100%; object-fit: contain; border-radius: 2px;
    border: 1px solid #1e3d00; max-height: 320px;
  }
  .article-body { font-family: 'Rajdhani', sans-serif; font-size: 0.88rem; color: #888; line-height: 1.65; }

  /* ── Pagination ── */
  .pagination {
    display: flex; align-items: center; justify-content: center; gap: 1rem;
    padding-top: 0.25rem;
  }
  .page-btn {
    display: inline-flex; align-items: center; gap: 0.25rem;
    background: none; border: 1px solid #1e3d00; border-radius: 2px;
    color: #3a6b00; font-family: 'Rajdhani', sans-serif; font-weight: 700;
    font-size: 0.6rem; letter-spacing: 0.12em; text-transform: uppercase;
    padding: 0.3rem 0.65rem; cursor: pointer;
    transition: color 0.15s, border-color 0.15s, background 0.15s;
  }
  .page-btn:hover:not(:disabled) { color: #6db800; border-color: #6db800; background: rgba(109,184,0,0.06); }
  .page-btn:disabled { opacity: 0.3; cursor: default; }
  .page-btn svg { width: 12px; height: 12px; }
  .page-info { display: flex; align-items: baseline; gap: 0.3rem; font-family: 'Rajdhani', sans-serif; }
  .page-current { font-size: 0.9rem; font-weight: 700; color: #6db800; }
  .page-sep     { font-size: 0.6rem; color: #1e3d00; }
  .page-total   { font-size: 0.7rem; font-weight: 600; color: #2a5200; }
</style>
